// src/features/landing/presentation/pages/RbBridgePage.jsx
// RB 브릿지(라이징브로커즈 M&A 사업부) 소개 페이지 — 회사소개서(RB bridge Company Profile, 2026.09) 구성을
// 라이징브로커즈 톤(크림 배경·오렌지·브라운)으로 옮긴 것. 상세 랜딩은 정적 HTML /bridge/mna.
import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Footer } from "../components/Footer";
import { RbBridgeConsultSection } from "../components/RbBridgeConsultSection";
import "../styles/landing.css";

// 정적 파일이라 react-router Link가 아닌 일반 <a>로 이동해야 한다
const LANDING_URL = "/bridge/mna";

const ICONS = {
    bridge: <><path d="M3 18h18" /><path d="M7 18V6" /><path d="M17 18V6" /><path d="M7 7l-4 7M7 7l4 7M17 7l-4 7M17 7l4 7" /></>,
    target: <><circle cx="12" cy="12" r="8" /><circle cx="12" cy="12" r="4" /><circle cx="12" cy="12" r="0.8" /></>,
    gap: <><path d="M4 6v12M20 6v12" /><path d="M8 12h8" strokeDasharray="2 2.5" /></>,
    desk: <><rect x="4" y="5" width="16" height="11" rx="1.5" /><path d="M9 20h6M12 16v4" /></>,
    rule: <><path d="M6 3h9l3 3v15H6z" /><path d="M9 10h6M9 14h6M9 18h3" /></>,
    chart: <><path d="M4 20h16" /><path d="M7 16v-4M12 16V8M17 16v-7" /></>,
    clock: <><circle cx="12" cy="12" r="8" /><path d="M12 8v4l3 2" /></>,
    seller: <><path d="M4 12h12" /><path d="M12 8l4 4-4 4" /><path d="M20 5v14" /></>,
    buyer: <><path d="M20 12H8" /><path d="M12 8l-4 4 4 4" /><path d="M4 5v14" /></>,
    file: <><path d="M6 3h9l3 3v15H6z" /><path d="M9 13l2 2 4-4" /></>,
    badge: <><circle cx="12" cy="9" r="5" /><path d="M9 13.5L8 21l4-2 4 2-1-7.5" /></>,
    link: <><path d="M10 14a4 4 0 0 0 5.66 0l3-3a4 4 0 0 0-5.66-5.66l-1 1" /><path d="M14 10a4 4 0 0 0-5.66 0l-3 3a4 4 0 0 0 5.66 5.66l1-1" /></>,
    room: <><path d="M4 21V4h12v17" /><path d="M16 9h4v12" /><circle cx="13" cy="13" r="0.8" /></>,
    mask: <><rect x="3" y="6" width="18" height="12" rx="1.5" /><path d="M7 12h10" strokeWidth="3" /></>,
    eye: <><path d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6S2 12 2 12z" /><circle cx="12" cy="12" r="2.5" /></>,
    pen: <><path d="M4 20l4-1 11-11-3-3L5 16z" /><path d="M14 7l3 3" /></>,
    lock: <><rect x="5" y="10" width="14" height="10" rx="1.5" /><path d="M8 10V7a4 4 0 0 1 8 0v3" /></>,
    door: <><path d="M6 21V3h12v18" /><path d="M3 21h18" /><circle cx="14.5" cy="12" r="0.8" /></>,
    team: <><circle cx="9" cy="8" r="3" /><circle cx="17" cy="9" r="2.5" /><path d="M3 20c0-3.5 2.7-6 6-6s6 2.5 6 6" /><path d="M15 14.5c3 0 6 1.8 6 5.5" /></>,
    scale: <><path d="M12 4v16M7 20h10" /><path d="M5 8h14" /><path d="M5 8l-2.5 6h5zM19 8l-2.5 6h5z" /></>,
};

function Icon({ name }) {
    return (
        <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor"
             strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            {ICONS[name]}
        </svg>
    );
}

// 회사소개서의 "원형 아이콘 점" — 라이징브로커즈 톤에 맞춰 오렌지/브라운 두 가지
function Dot({ icon, tone = "orange", size = "md" }) {
    return (
        <span className={`rbp-dot rbp-dot--${tone} rbp-dot--${size}`}>
            <Icon name={icon} />
        </span>
    );
}

// 회사소개서 각 장 상단의 "점 ── 영문 라벨" 헤어라인
function SectionRule({ icon, tone, label }) {
    return (
        <div className="rbp-rule">
            <Dot icon={icon} tone={tone} />
            <span className="rbp-rule__line" />
            <span className="rbp-rule__label">{label}</span>
        </div>
    );
}

const CONTENTS = [
    { id: "summary", no: "01", en: "Executive Summary", ko: "네 가지 핵심 축", icon: "desk" },
    { id: "market", no: "02", en: "Market Dynamics", ko: "시장 환경의 변화", icon: "chart" },
    { id: "position", no: "03", en: "Market Positioning", ko: "우리의 타겟 시장", icon: "target" },
    { id: "value", no: "04", en: "Value Proposition", ko: "가치 제안", icon: "scale" },
    { id: "platform", no: "05", en: "Platform Mechanism", ko: "4단계 프라이빗 딜", icon: "file" },
    { id: "security", no: "06", en: "Security System", ko: "정보 보안 체계", icon: "lock" },
    { id: "process", no: "07", en: "Consulting Process", ko: "10단계 컨설팅", icon: "pen" },
    { id: "expertise", no: "08", en: "Expertise & Partnerships", ko: "전문성 및 파트너십", icon: "team" },
];

const SUMMARY = [
    {
        icon: "target",
        label: "RB Bridge의 미션",
        title: "보험업계의 비효율을\n데이터와 제도권으로",
        body: "라이징브로커즈가 15년 이상 쌓은 노하우와 데이터 검증 시스템으로 보험업계 조직 거래와 인재 매칭을 제도권 시장으로 끌어올립니다.",
    },
    {
        icon: "chart",
        label: "핵심 가치",
        title: "3억~20억 원 규모\n소형 지사 거래의 제도화",
        body: "3억 원에서 20억 원 사이의 소형 보험 지사 거래를 위한 프라이빗 딜 플랫폼입니다.",
    },
    {
        icon: "gap",
        label: "차별성",
        title: "사각지대의 해소",
        body: "대형 회계법인과 투자은행이 주도하는 시장, 그리고 일반 구인구직 플랫폼 사이에서 다뤄지지 않던 거래를 맡습니다.",
    },
    {
        icon: "desk",
        label: "통합 딜 데스크",
        title: "검증 · 매칭 · 클로징의 결합",
        body: "익명 검증, 인수자 매칭, 비공개 대면 클로징을 하나의 데스크에서 진행합니다.",
    },
];

const DRIVERS = [
    {
        icon: "rule",
        no: "DRIVER 01",
        title: "규제 패러다임 변화",
        body: "1200% 룰의 적용 범위가 GA로 확대되며, 지사 운영자들은 초기 정착지원금과 시책 지급에서 현금 유동성 압박을 받고 있습니다.",
    },
    {
        icon: "chart",
        no: "DRIVER 02",
        title: "경쟁 심화",
        body: "자본력이 부족한 소형 지사는 유기적 성장의 한계에 부딪혔고, 조직 단위 인수라는 새로운 방식이 자리 잡고 있습니다.",
    },
    {
        icon: "clock",
        no: "DRIVER 03",
        title: "매각 수요의 누적",
        body: "오랫동안 조직을 이끌어 온 지사장의 고령화와 건강 문제, 분명하지 않은 후계 구조가 맞물려 매각 수요가 늘고 있습니다.",
    },
];

const PLATFORM_STEPS = [
    { icon: "file", title: "매물 등록", body: "매도자가 익명으로 정보를 등록하면 운영팀이 조직 규모·월초 보험료 등을 익명 요약 카드로 정리합니다." },
    { icon: "badge", title: "인수자 인증", body: "자본력과 인수 목적에 대한 사전 인증 절차를 통과한 인수자만 요약 정보를 열람할 수 있습니다." },
    { icon: "link", title: "프라이빗 매칭", body: "실제 실적과 수수료 흐름 같은 핵심 데이터는 NDA 체결 후 단계적으로 공개해 정보 비대칭을 줄입니다." },
    { icon: "room", title: "오프라인 클로징", body: "프라이빗 미팅룸에서 최종 협상을 진행하고, 양측 사이의 소통을 직접 조율합니다." },
];

const SECURITY = [
    { icon: "mask", title: "시스템 마스킹", body: "조직을 특정할 수 있는 정보는 등록 단계에서 가립니다." },
    { icon: "eye", title: "운영팀 검수", body: "공개 전 모든 내용을 사람이 한 번 더 확인합니다." },
    { icon: "pen", title: "NDA 체결", body: "상세 정보는 비밀유지계약 이후에만 공개합니다." },
    { icon: "lock", title: "열람 권한 관리", body: "누가 어떤 정보를 봤는지 기록하고 필요한 범위만 엽니다." },
    { icon: "door", title: "오프라인 공간 분리", body: "핵심 협상은 온라인이 아닌 대면으로 진행합니다." },
];

const PROCESS = [
    "매물 발굴 및 상담",
    "기초자료 수집 및\n사전검토",
    "가치평가 및\n거래구조 설계",
    "자문계약 체결 및\n매각자료 작성",
    "인수 후보\n발굴 및 매칭",
    "미팅 · LOI 제출 ·\n조건 협의(MOU)",
    "실사(DD) 진행\n및 지원",
    "최종 조건 협상 및\n본계약(양수도계약) 체결",
    "클로징 및\n수수료 정산",
    "인수 후 통합(PMI)",
];

function Lines({ text }) {
    return text.split("\n").map((line, i, arr) => (
        <React.Fragment key={i}>
            {line}
            {/* 모바일에서 <br>을 숨겨도 단어가 붙지 않도록 공백을 함께 둔다 */}
            {i < arr.length - 1 && <>{" "}<br /></>}
        </React.Fragment>
    ));
}

function CtaButtons({ dark = false }) {
    return (
        <div className="rbp-cta">
            {/* 표지 아래 매수자·매도자·파트너스 상담 박스(#consult)로 이동 */}
            <a href="#consult" className="rbp-btn rbp-btn--primary">
                비공개 상담 신청
            </a>
            <a href={LANDING_URL} className={`rbp-btn ${dark ? "rbp-btn--ghost-dark" : "rbp-btn--ghost"}`}>
                RB Bridge 자세히 보기
            </a>
        </div>
    );
}

// 사장교(케이블 브릿지) 라인 드로잉 — 회사소개서 표지의 다리 사진 자리
function BridgeIllustration() {
    const cablesL = [0, 1, 2, 3, 4, 5, 6];
    return (
        <svg className="rbp-bridge-svg" viewBox="0 0 480 360" aria-hidden="true">
            <rect x="0" y="0" width="480" height="360" fill="#f7f1ec" />
            <circle cx="372" cy="92" r="46" fill="#fbe3d2" />
            {/* 주탑 */}
            <path d="M232 40 L222 268 M248 40 L258 268" stroke="#1c1814" strokeWidth="5" strokeLinecap="round" />
            <path d="M226 150 H254" stroke="#1c1814" strokeWidth="4" />
            {/* 케이블 */}
            {cablesL.map((i) => (
                <React.Fragment key={i}>
                    <line x1="232" y1={56 + i * 12} x2={200 - i * 30} y2="236" stroke="#f16102" strokeWidth="1.4" />
                    <line x1="248" y1={56 + i * 12} x2={280 + i * 30} y2="236" stroke="#c2926b" strokeWidth="1.4" />
                </React.Fragment>
            ))}
            {/* 상판 */}
            <rect x="0" y="234" width="480" height="10" fill="#1c1814" />
            {/* 수면 */}
            <rect x="0" y="244" width="480" height="116" fill="#efe6de" />
            <path d="M40 290 H140 M200 306 H300 M340 284 H440 M90 326 H170 M260 336 H380" stroke="#d9c8b9" strokeWidth="2" strokeLinecap="round" />
        </svg>
    );
}

export function RbBridgePage() {
    // 다른 페이지에서 /rb-bridge#consult 로 들어오면 ScrollToTop(맨 위 이동) 뒤에 해당 앵커로 스크롤한다.
    // (React Router는 해시 스크롤을 하지 않음) 대상 요소의 scroll-margin-top이 Navbar 오프셋을 맞춘다.
    const { hash } = useLocation();
    useEffect(() => {
        if (!hash) return;
        const id = decodeURIComponent(hash.slice(1));
        const raf = requestAnimationFrame(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" }));
        return () => cancelAnimationFrame(raf);
    }, [hash]);

    return (
        <>
            <main className="rbp">
                {/* 1. 표지 */}
                <section className="rbp-hero">
                    <div className="rbp-container">
                        <div className="rbp-hero__top">
                            <span className="rbp-brandmark">RB Bridge</span>
                            <span className="rbp-meta">라이징브로커즈 M&amp;A 사업부</span>
                        </div>
                        <div className="rbp-hero__grid">
                            <div className="rbp-hero__word">
                                <h1 className="rbp-hero__title" aria-label="RB Bridge">
                                    <span className="rbp-hero__rb">RB<Dot icon="team" tone="orange" size="sm" /></span>
                                    <span className="rbp-hero__bridge">bridge<Dot icon="file" tone="brown" size="sm" /></span>
                                </h1>
                            </div>
                            <div className="rbp-hero__side">
                                <p className="rbp-hero__kicker">보험 GA M&amp;A 전문 컨설팅</p>
                                <p className="rbp-hero__en">Strategic Advisory for General Agency &amp; M&amp;A</p>
                                <div className="rbp-hero__visual">
                                    <BridgeIllustration />
                                </div>
                            </div>
                        </div>
                        <div className="rbp-hero__bottom">
                            <Dot icon="bridge" tone="orange" />
                            <span className="rbp-rule__line" />
                            <blockquote className="rbp-hero__quote">
                                두 지점을 잇는 다리처럼, 서로 다른 조직과 사람이{" "}
                                <br />
                                가장 안전한 길로 연결되는 신뢰의 기반이 됩니다.
                            </blockquote>
                        </div>
                    </div>
                </section>

                {/* 1-1. 상담 신청 — 표지와 구분되는 크림 띠 */}
                <div className="rbp-consult-band">
                    <div className="rbp-container">
                        <RbBridgeConsultSection />
                    </div>
                </div>

                {/* 2. 목차 */}
                <nav className="rbp-contents" aria-label="RB Bridge 소개 목차">
                    <div className="rbp-container">
                        <div className="rbp-contents__head">
                            <h2 className="rbp-contents__title">Company <br />Profile</h2>
                            <p className="rbp-contents__tag">
                                조직과 사람 사이, 가장 안전하게 놓인 다리
                                <span>Professionalism · Trust · Growth</span>
                            </p>
                        </div>
                        <ol className="rbp-contents__list">
                            {CONTENTS.map((c, i) => (
                                <li key={c.id}>
                                    <a href={`#${c.id}`}>
                                        <Dot icon={c.icon} tone={i % 2 === 0 ? "orange" : "brown"} size="sm" />
                                        <span className="rbp-contents__en">{c.no} · {c.en}</span>
                                        <span className="rbp-contents__ko">{c.ko}</span>
                                    </a>
                                </li>
                            ))}
                        </ol>
                    </div>
                </nav>

                {/* 3. Executive Summary */}
                <section id="summary" className="rbp-section rbp-section--cream">
                    <div className="rbp-container">
                        <h2 className="rbp-title">RB Bridge의 네 가지 핵심 축</h2>
                        <SectionRule icon="desk" tone="orange" label="Executive Summary" />
                        <div className="rbp-cols rbp-cols--4">
                            {SUMMARY.map((s, i) => (
                                <article key={s.label} className="rbp-col">
                                    <p className="rbp-col__label">
                                        <Icon name={s.icon} />
                                        0{i + 1} · {s.label}
                                    </p>
                                    <h3 className="rbp-col__title"><Lines text={s.title} /></h3>
                                    <p className="rbp-col__body">{s.body}</p>
                                </article>
                            ))}
                        </div>
                    </div>
                </section>

                {/* 4. Market Dynamics */}
                <section id="market" className="rbp-section">
                    <div className="rbp-container">
                        <div className="rbp-split">
                            <div>
                                <h2 className="rbp-title rbp-title--lg">지사 매각이 늘어나는 <br />세 가지 이유.</h2>
                                <p className="rbp-lead">세 가지 요인이 겹치면서 조직 단위 거래가 늘고 있지만, 대부분 제도권 밖에서 이뤄지고 있습니다.</p>
                            </div>
                            <div className="rbp-split__aside">
                                <SectionRule icon="chart" tone="brown" label="Market Dynamics" />
                            </div>
                        </div>
                        <div className="rbp-cols rbp-cols--3">
                            {DRIVERS.map((d) => (
                                <article key={d.no} className="rbp-col">
                                    <p className="rbp-col__label"><Icon name={d.icon} />{d.no}</p>
                                    <h3 className="rbp-col__title">{d.title}</h3>
                                    <p className="rbp-col__body">{d.body}</p>
                                </article>
                            ))}
                        </div>
                        <p className="rbp-note">시행 시기와 내용은 관계 법령·감독규정에 따라 달라질 수 있습니다.</p>
                    </div>
                </section>

                {/* 5. Market Positioning */}
                <section id="position" className="rbp-section rbp-section--cream">
                    <div className="rbp-container">
                        <div className="rbp-gap">
                            <div className="rbp-gap__head">
                                <p className="rbp-eyebrow">Market Gap · 시장의 사각지대</p>
                                <h2 className="rbp-title rbp-title--lg">두 거대 시장 사이의 <br />비어 있는 자리.</h2>
                            </div>
                            <div className="rbp-scale" role="img" aria-label="개인 단위 채용 시장과 100억 원 이상 대형 M&A 시장 사이, 3억~20억 원 구간이 RB Bridge의 영역">
                                <div className="rbp-scale__cell">
                                    <span className="rbp-scale__size">개인 단위</span>
                                    <span className="rbp-scale__name">채용 · 지인 소개</span>
                                </div>
                                <div className="rbp-scale__cell rbp-scale__cell--on">
                                    <span className="rbp-scale__size">3억~20억 원</span>
                                    <span className="rbp-scale__name">RB Bridge</span>
                                </div>
                                <div className="rbp-scale__cell">
                                    <span className="rbp-scale__size">100억 원 이상</span>
                                    <span className="rbp-scale__name">대형 M&amp;A</span>
                                </div>
                            </div>
                        </div>
                        <div className="rbp-cols rbp-cols--3 rbp-cols--top">
                            <article className="rbp-col">
                                <p className="rbp-col__label"><Icon name="gap" />시장의 공백</p>
                                <h3 className="rbp-col__title">대형 M&amp;A와 개인 채용 사이의 거대한 공백</h3>
                                <p className="rbp-col__body">두 시장 모두 이 규모의 지사 거래를 다루지 않아, 그동안 지인 소개에 기대 왔습니다.</p>
                            </article>
                            <article className="rbp-col">
                                <p className="rbp-col__label"><Icon name="target" />명확한 타겟</p>
                                <h3 className="rbp-col__title">3억 원에서 20억 원 구간에 집중</h3>
                                <p className="rbp-col__body">RB Bridge는 이 공백에 해당하는 소형 보험 지사 거래만을 전문으로 다룹니다.</p>
                            </article>
                            <article className="rbp-col">
                                <p className="rbp-col__label"><Icon name="clock" />비효율 해소</p>
                                <h3 className="rbp-col__title">조직장들의 기회비용을 줄입니다</h3>
                                <p className="rbp-col__body">전문 중개자가 없어 거래 완료까지 6개월 이상 걸리던 기회비용과 시장의 불투명성을 줄입니다.</p>
                            </article>
                        </div>
                    </div>
                </section>

                {/* 6. Value Proposition */}
                <section id="value" className="rbp-section">
                    <div className="rbp-container">
                        <h2 className="rbp-title">양도하는 쪽과 인수하는 쪽, <br />각자에게 필요한 것</h2>
                        <p className="rbp-lead rbp-lead--quote">어느 한쪽에 치우치지 않고 양측의 조건을 조율합니다.</p>
                        <SectionRule icon="scale" tone="orange" label="Value Proposition" />
                        <div className="rbp-cols rbp-cols--2">
                            <article className="rbp-col">
                                <p className="rbp-col__label rbp-col__label--orange"><Icon name="seller" />For Sellers · 양도하는 쪽</p>
                                <h3 className="rbp-col__title rbp-col__title--lg">전략적 엑시트</h3>
                                <p className="rbp-col__body">
                                    1200% 룰 시행과 수수료 분급 확대가 더해진 환경에서, 소형 대리점과 지사의 운영 리스크는 혼자 감당하기 어려운 무게가 됐습니다.
                                    지금의 양도는 달라진 환경에서 내리는 전략적 판단입니다.
                                </p>
                            </article>
                            <article className="rbp-col">
                                <p className="rbp-col__label rbp-col__label--brown"><Icon name="buyer" />For Buyers · 인수하는 쪽</p>
                                <h3 className="rbp-col__title rbp-col__title--lg">전략적 인수</h3>
                                <p className="rbp-col__body">
                                    안정적인 수익 사업을 인수하려는 개인 투자자, 소형 조직을 흡수해 보험사와의 협상력을 높이려는 법인 GA에게
                                    검수를 거친 조직을 소개합니다.
                                </p>
                            </article>
                        </div>
                    </div>
                </section>

                {/* 7. Platform Mechanism */}
                <section id="platform" className="rbp-section rbp-section--cream">
                    <div className="rbp-container">
                        <div className="rbp-split">
                            <h2 className="rbp-title rbp-title--lg">비공개로 진행되는 <br />4단계 거래 절차</h2>
                            <div className="rbp-split__aside">
                                <p className="rbp-aside-text">모든 거래는 검증된 절차에 따라 비공개로 진행됩니다.</p>
                                <span className="rbp-rule__label">Platform Mechanism</span>
                            </div>
                        </div>
                        <ol className="rbp-steps">
                            {PLATFORM_STEPS.map((s, i) => (
                                <li key={s.title} className="rbp-step">
                                    <div className="rbp-step__head">
                                        <Dot icon={s.icon} tone={i % 2 === 0 ? "orange" : "brown"} />
                                    </div>
                                    <p className="rbp-step__no">STEP 0{i + 1}</p>
                                    <h3 className="rbp-step__title">{s.title}</h3>
                                    <p className="rbp-col__body">{s.body}</p>
                                </li>
                            ))}
                        </ol>
                    </div>
                </section>

                {/* 8. Security System */}
                <section id="security" className="rbp-section rbp-section--dark">
                    <div className="rbp-container rbp-security">
                        <div className="rbp-security__intro">
                            <p className="rbp-eyebrow rbp-eyebrow--light">Security System</p>
                            <h2 className="rbp-title rbp-title--lg rbp-title--light">다섯 단계로 <br />정보를 보호합니다</h2>
                            <p className="rbp-lead rbp-lead--light">정보 보호를 가장 먼저 설계했습니다.</p>
                            <div className="rbp-risk">
                                <p className="rbp-risk__label"><Icon name="lock" />Risk of Exposure · 노출의 위험</p>
                                <p className="rbp-risk__body">
                                    정보가 노출되는 순간 소속 인력이 이탈하거나 거래가 무산될 수 있습니다.
                                    RB Bridge는 이 위험을 절차와 공간 모두에서 막습니다.
                                </p>
                            </div>
                        </div>
                        <ul className="rbp-security__list">
                            {SECURITY.map((s) => (
                                <li key={s.title}>
                                    <span className="rbp-security__icon"><Icon name={s.icon} /></span>
                                    <span className="rbp-security__title">{s.title}</span>
                                    <span className="rbp-security__body">{s.body}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </section>

                {/* 9. Consulting Process */}
                <section id="process" className="rbp-section">
                    <div className="rbp-container">
                        <div className="rbp-split">
                            <div>
                                <h2 className="rbp-title rbp-title--lg">첫 상담부터 인수 후 통합까지, <br />10단계로 진행합니다.</h2>
                                <p className="rbp-lead">처음 상담한 담당자가 거래가 끝날 때까지 맡습니다.</p>
                            </div>
                            <div className="rbp-split__aside">
                                <SectionRule icon="pen" tone="brown" label="Consulting Process" />
                            </div>
                        </div>
                        <ol className="rbp-process">
                            {PROCESS.map((p, i) => (
                                <li key={p} className="rbp-process__item">
                                    <span className={`rbp-process__no rbp-process__no--${i % 2 === 0 ? "orange" : "brown"}`}>
                                        {String(i + 1).padStart(2, "0")}
                                    </span>
                                    <span className="rbp-process__name"><Lines text={p} /></span>
                                </li>
                            ))}
                        </ol>
                    </div>
                </section>

                {/* 10. Expertise & Partnerships */}
                <section id="expertise" className="rbp-section rbp-section--cream">
                    <div className="rbp-container rbp-expertise">
                        <div>
                            <h2 className="rbp-title rbp-title--en">Expertise &amp; <br />Partnerships</h2>
                            <p className="rbp-lead">검증 체계를 갖춘 조직 중개로 보험 산업의 구조 재편을 돕습니다.</p>
                        </div>
                        <div className="rbp-pillars">
                            <article className="rbp-pillar">
                                <Dot icon="team" tone="orange" />
                                <div>
                                    <p className="rbp-step__no">PILLAR 01</p>
                                    <h3 className="rbp-col__title">자체 전문가 조직 운영</h3>
                                    <p className="rbp-col__body">가치평가부터 클로징까지 거래 실무는 자체 전문가 조직이 직접 맡습니다.</p>
                                </div>
                            </article>
                            <article className="rbp-pillar">
                                <Dot icon="link" tone="brown" />
                                <div>
                                    <p className="rbp-step__no">PILLAR 02</p>
                                    <h3 className="rbp-col__title">외부 파트너십 연계</h3>
                                    <p className="rbp-col__body">법무·세무·노무 검토는 제휴 전문가와 초기부터 함께 진행합니다.</p>
                                </div>
                            </article>
                        </div>
                    </div>
                </section>

                {/* 11. Closing */}
                <section className="rbp-section rbp-closing">
                    <div className="rbp-container">
                        <div className="rbp-split">
                            <h2 className="rbp-closing__title">공정하고 중립적인 <br />전문가 그룹</h2>
                            <div className="rbp-split__aside">
                                <SectionRule icon="scale" tone="orange" label="RB Bridge · 2026" />
                            </div>
                        </div>
                        <div className="rbp-cols rbp-cols--2 rbp-cols--top">
                            <p className="rbp-col__body rbp-closing__body">
                                RB Bridge는 라이징브로커즈가 15년 이상 쌓아 온 보험업계 노하우와 데이터 기반 검증 시스템을 뿌리로 삼아,
                                매도자와 매수자 어느 한쪽으로도 치우치지 않습니다.
                            </p>
                            <p className="rbp-col__body rbp-closing__body">
                                보험업계 인재 매칭과 보험 지사 거래에서 믿고 맡길 수 있는 기준이 되겠습니다.
                            </p>
                        </div>
                        <div className="rbp-closing__cta">
                            <div>
                                <p className="rbp-closing__cta-title">결정은 나중에. 이야기는 비공개로 먼저.</p>
                                <p className="rbp-closing__cta-sub">
                                    조직명 없이 연락처만으로 상담을 시작하실 수 있습니다. 상담만으로 등록되지 않습니다.{" "}
                                    <br />
                                    대표번호 <a href="tel:02-858-2014">02-858-2014</a>
                                </p>
                            </div>
                            <CtaButtons dark />
                        </div>
                    </div>
                </section>
            </main>
            <Footer />

            <style>{`
                .rbp {
                    --rbp-ink: #1c1814;
                    --rbp-text: #4a433d;
                    --rbp-muted: #8a8079;
                    --rbp-line: #e5ddd7;
                    --rbp-cream: #f7f4f1;
                    --rbp-orange: #f16102;
                    --rbp-orange-dark: #d95502;
                    --rbp-orange-soft: #fdeee4;
                    --rbp-brown: #c2926b;
                    --rbp-brown-soft: #f4ebe3;
                    color: var(--rbp-ink);
                    background: #fff;
                    text-align: left; /* App.css의 .App { text-align: center } 상속 차단 */
                    word-break: keep-all;
                    overflow-wrap: break-word;
                }
                .rbp *, .rbp *::before, .rbp *::after { box-sizing: border-box; }
                .rbp-container { max-width: 1200px; margin: 0 auto; padding: 0 40px; }

                /* 공통: 원형 아이콘 점 */
                .rbp-dot {
                    display: inline-flex; align-items: center; justify-content: center;
                    flex-shrink: 0; border-radius: 50%;
                    width: 48px; height: 48px;
                }
                .rbp-dot--sm { width: 34px; height: 34px; }
                .rbp-dot--sm svg { width: 17px; height: 17px; }
                .rbp-dot--orange { background: var(--rbp-orange); color: #fff; }
                .rbp-dot--brown { background: var(--rbp-brown); color: #fff; }

                /* 공통: 점 ── 영문 라벨 헤어라인 */
                .rbp-rule { display: flex; align-items: center; gap: 20px; margin: 28px 0 48px; }
                .rbp-rule__line { flex: 1; height: 1px; background: var(--rbp-ink); opacity: 0.18; }
                .rbp-rule__label { font-size: 13px; font-weight: 700; color: var(--rbp-ink); letter-spacing: 0.01em; white-space: nowrap; }

                .rbp-section { padding: 120px 0; }
                .rbp-section--cream { background: var(--rbp-cream); }
                .rbp-section--dark { background: var(--rbp-ink); color: #fff; }

                .rbp-eyebrow {
                    margin: 0 0 16px; font-size: 13px; font-weight: 700; letter-spacing: 0.08em;
                    text-transform: uppercase; color: var(--rbp-brown);
                }
                .rbp-eyebrow--light { color: #e2b893; }
                .rbp-title {
                    margin: 0; font-size: clamp(26px, 3vw, 38px); line-height: 1.3;
                    font-weight: 800; letter-spacing: -0.02em; color: var(--rbp-ink);
                }
                .rbp-title--lg { font-size: clamp(30px, 3.6vw, 46px); line-height: 1.22; }
                .rbp-title--en { font-size: clamp(40px, 5vw, 64px); line-height: 1.05; letter-spacing: -0.03em; }
                .rbp-title--light { color: #fff; }
                .rbp-lead { margin: 20px 0 0; font-size: 16px; line-height: 1.75; color: var(--rbp-text); }
                .rbp-lead--quote { color: var(--rbp-muted); }
                .rbp-lead--light { color: rgba(255, 255, 255, 0.72); }
                .rbp-note { margin: 40px 0 0; font-size: 13px; color: var(--rbp-muted); }

                /* 제목 + 오른쪽 헤어라인 2단 */
                .rbp-split {
                    display: grid; grid-template-columns: minmax(0, 1.15fr) minmax(0, 1fr);
                    gap: 48px; align-items: end; margin-bottom: 56px;
                }
                .rbp-split .rbp-rule { margin: 0; }
                .rbp-split__aside { display: flex; flex-direction: column; align-items: flex-end; gap: 10px; }
                .rbp-split__aside .rbp-rule { width: 100%; }
                .rbp-aside-text { margin: 0; font-size: 16px; font-weight: 700; color: var(--rbp-ink); text-align: right; }
                .rbp-split__aside .rbp-rule__label { color: var(--rbp-muted); font-weight: 600; }

                /* 세로 헤어라인으로 나뉜 열 */
                .rbp-cols { display: grid; }
                .rbp-cols--4 { grid-template-columns: repeat(4, minmax(0, 1fr)); }
                .rbp-cols--3 { grid-template-columns: repeat(3, minmax(0, 1fr)); }
                .rbp-cols--2 { grid-template-columns: repeat(2, minmax(0, 1fr)); }
                .rbp-cols--top { margin-top: 64px; }
                .rbp-col { padding: 0 28px; border-left: 1px solid var(--rbp-line); }
                .rbp-col:first-child { padding-left: 0; border-left: 0; }
                .rbp-col:last-child { padding-right: 0; }
                .rbp-col__label {
                    display: flex; align-items: center; gap: 8px;
                    margin: 0 0 18px; font-size: 13px; font-weight: 700; color: var(--rbp-ink);
                }
                .rbp-col__label svg { width: 18px; height: 18px; color: var(--rbp-brown); }
                .rbp-col__label--orange, .rbp-col__label--orange svg { color: var(--rbp-orange); }
                .rbp-col__label--brown, .rbp-col__label--brown svg { color: var(--rbp-brown); }
                .rbp-col__title {
                    margin: 0 0 14px; font-size: 19px; line-height: 1.45; font-weight: 800;
                    letter-spacing: -0.01em; color: var(--rbp-ink);
                }
                .rbp-col__title--lg { font-size: 26px; }
                .rbp-col__body { margin: 0; font-size: 15px; line-height: 1.8; color: var(--rbp-text); }

                /* 1. 표지 */
                .rbp-hero { padding: 56px 0 96px; background: #fff; }
                .rbp-hero__top {
                    display: flex; justify-content: space-between; align-items: center;
                    padding-bottom: 20px; border-bottom: 1px solid var(--rbp-line);
                }
                .rbp-brandmark { font-size: 15px; font-weight: 800; color: var(--rbp-ink); }
                .rbp-meta { font-size: 13px; color: var(--rbp-muted); letter-spacing: 0.02em; }
                .rbp-hero__grid {
                    display: grid; grid-template-columns: minmax(0, 1.25fr) minmax(0, 1fr);
                    gap: 48px; align-items: center; padding: 56px 0 48px;
                }
                .rbp-hero__title {
                    margin: 0; font-weight: 800; color: var(--rbp-ink);
                    font-size: clamp(88px, 11.5vw, 164px); line-height: 0.92; letter-spacing: -0.05em;
                }
                .rbp-hero__rb, .rbp-hero__bridge { display: flex; align-items: flex-start; }
                .rbp-hero__bridge { padding-left: 0.4em; }
                .rbp-hero__title .rbp-dot { margin: 0.12em 0 0 -0.04em; width: 0.32em; height: 0.32em; }
                .rbp-hero__title .rbp-dot svg { width: 46%; height: 46%; }
                .rbp-hero__kicker { margin: 0; font-size: 22px; font-weight: 800; color: var(--rbp-ink); }
                .rbp-hero__en { margin: 6px 0 24px; font-size: 13px; color: var(--rbp-muted); }
                .rbp-hero__visual { border-radius: 4px; overflow: hidden; line-height: 0; }
                .rbp-bridge-svg { width: 100%; height: auto; display: block; }
                .rbp-hero__bottom { display: flex; align-items: center; gap: 24px; }
                .rbp-hero__quote {
                    margin: 0; max-width: 520px; font-size: 15px; line-height: 1.75; color: var(--rbp-text);
                }

                .rbp-cta { display: flex; gap: 12px; flex-wrap: wrap; flex-shrink: 0; }
                .rbp-btn {
                    display: inline-flex; align-items: center; justify-content: center;
                    padding: 14px 24px; border-radius: 8px; font-size: 15px; font-weight: 700;
                    text-decoration: none; white-space: nowrap; transition: background 0.2s ease, color 0.2s ease, border-color 0.2s ease;
                }
                .rbp-btn--primary { background: var(--rbp-orange); color: #fff; border: 1px solid var(--rbp-orange); }
                .rbp-btn--primary:hover { background: var(--rbp-orange-dark); border-color: var(--rbp-orange-dark); }
                .rbp-btn--ghost { background: #fff; color: var(--rbp-ink); border: 1px solid var(--rbp-line); }
                .rbp-btn--ghost:hover { border-color: var(--rbp-brown); color: var(--rbp-brown); }
                .rbp-btn--ghost-dark { background: transparent; color: #fff; border: 1px solid rgba(255, 255, 255, 0.35); }
                .rbp-btn--ghost-dark:hover { border-color: #fff; }

                /* 1-1. 상담 신청 띠 */
                .rbp-consult-band { padding: 88px 0 96px; background: var(--rbp-cream); border-top: 1px solid var(--rbp-line); border-bottom: 1px solid var(--rbp-line); }

                /* 2. 목차 */
                .rbp-contents { padding: 96px 0; }
                .rbp-contents__head { display: flex; justify-content: space-between; align-items: flex-start; gap: 40px; margin-bottom: 64px; }
                .rbp-contents__title {
                    margin: 0; font-size: clamp(44px, 5.4vw, 72px); line-height: 1.02;
                    font-weight: 800; letter-spacing: -0.04em; color: var(--rbp-ink);
                }
                .rbp-contents__tag { margin: 8px 0 0; font-size: 17px; font-weight: 700; color: var(--rbp-ink); text-align: right; }
                .rbp-contents__tag span { display: block; margin-top: 10px; font-size: 13px; font-weight: 500; color: var(--rbp-muted); }
                .rbp-contents__list {
                    list-style: none; margin: 0; padding: 0;
                    display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); row-gap: 56px;
                }
                .rbp-contents__list li { position: relative; }
                .rbp-contents__list li::before {
                    content: ""; position: absolute; left: 0; right: 0; top: 17px;
                    height: 1px; background: var(--rbp-ink); opacity: 0.15;
                }
                .rbp-contents__list a { position: relative; display: block; padding-right: 20px; text-decoration: none; color: inherit; }
                .rbp-contents__list .rbp-dot { position: relative; margin-bottom: 18px; }
                .rbp-contents__en { display: block; font-size: 12px; font-weight: 700; color: var(--rbp-muted); letter-spacing: 0.01em; }
                .rbp-contents__ko { display: block; margin-top: 6px; font-size: 16px; font-weight: 700; color: var(--rbp-ink); transition: color 0.2s ease; }
                .rbp-contents__list a:hover .rbp-contents__ko { color: var(--rbp-orange); }

                /* 5. 시장 사각지대 */
                .rbp-gap { display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1.1fr); gap: 56px; align-items: end; }
                .rbp-scale { display: grid; grid-template-columns: 1fr 1.25fr 1fr; border-top: 1px solid var(--rbp-ink); border-bottom: 1px solid var(--rbp-ink); }
                .rbp-scale__cell { display: flex; flex-direction: column; gap: 6px; padding: 28px 20px; }
                .rbp-scale__cell + .rbp-scale__cell { border-left: 1px solid var(--rbp-line); }
                .rbp-scale__cell--on { background: var(--rbp-orange); color: #fff; border-left-color: var(--rbp-orange) !important; }
                .rbp-scale__cell--on + .rbp-scale__cell { border-left-color: var(--rbp-orange); }
                .rbp-scale__size { font-size: 13px; font-weight: 700; color: var(--rbp-muted); }
                .rbp-scale__name { font-size: 18px; font-weight: 800; color: var(--rbp-ink); }
                .rbp-scale__cell--on .rbp-scale__size { color: rgba(255, 255, 255, 0.85); }
                .rbp-scale__cell--on .rbp-scale__name { color: #fff; }

                /* 7. 4단계 */
                .rbp-steps { list-style: none; margin: 0; padding: 0; display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); }
                .rbp-step { padding: 0 28px 0 0; }
                .rbp-step + .rbp-step { padding-left: 28px; border-left: 1px solid var(--rbp-line); }
                .rbp-step__head { position: relative; margin-bottom: 28px; }
                .rbp-step__head::after {
                    content: ""; position: absolute; left: 48px; right: -28px; top: 24px;
                    height: 1px; background: var(--rbp-ink); opacity: 0.18;
                }
                .rbp-step + .rbp-step .rbp-step__head::after { left: 48px; }
                .rbp-step:last-child .rbp-step__head::after { right: 0; }
                .rbp-step__no { margin: 0 0 6px; font-size: 12px; font-weight: 800; color: var(--rbp-brown); letter-spacing: 0.04em; }
                .rbp-step__title { margin: 0 0 12px; font-size: 20px; font-weight: 800; color: var(--rbp-ink); }

                /* 8. 보안 */
                .rbp-security { display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); gap: 80px; align-items: start; }
                .rbp-risk { margin-top: 48px; padding-top: 28px; border-top: 1px solid rgba(255, 255, 255, 0.16); }
                .rbp-risk__label { display: flex; align-items: center; gap: 8px; margin: 0 0 12px; font-size: 13px; font-weight: 700; color: #e2b893; }
                .rbp-risk__label svg { width: 18px; height: 18px; }
                .rbp-risk__body { margin: 0; font-size: 15px; line-height: 1.8; color: rgba(255, 255, 255, 0.72); }
                .rbp-security__list { list-style: none; margin: 0; padding: 0; }
                .rbp-security__list li {
                    display: grid; grid-template-columns: 40px 150px minmax(0, 1fr); align-items: center;
                    gap: 16px; padding: 24px 0; border-bottom: 1px solid rgba(255, 255, 255, 0.14);
                }
                .rbp-security__list li:first-child { border-top: 1px solid rgba(255, 255, 255, 0.14); }
                .rbp-security__icon { color: var(--rbp-orange); line-height: 0; }
                .rbp-security__title { font-size: 18px; font-weight: 800; color: #fff; }
                .rbp-security__body { font-size: 14px; line-height: 1.6; color: rgba(255, 255, 255, 0.66); }

                /* 9. 10단계 */
                .rbp-process {
                    list-style: none; margin: 0; padding: 0;
                    display: grid; grid-template-columns: repeat(5, minmax(0, 1fr)); row-gap: 48px;
                }
                .rbp-process__item { position: relative; padding-right: 16px; }
                .rbp-process__item::before {
                    content: ""; position: absolute; left: 0; right: 0; top: 17px;
                    height: 1px; background: var(--rbp-ink); opacity: 0.15;
                }
                .rbp-process__no {
                    position: relative; display: inline-flex; align-items: center; justify-content: center;
                    width: 34px; height: 34px; border-radius: 50%;
                    font-size: 12px; font-weight: 800; color: #fff;
                }
                .rbp-process__no--orange { background: var(--rbp-orange); }
                .rbp-process__no--brown { background: var(--rbp-brown); }
                .rbp-process__name { display: block; margin-top: 18px; font-size: 16px; line-height: 1.45; font-weight: 700; color: var(--rbp-ink); }

                /* 10. 전문성 */
                .rbp-expertise { display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); gap: 80px; align-items: start; }
                .rbp-pillars { display: flex; flex-direction: column; }
                .rbp-pillar { display: flex; gap: 24px; padding: 32px 0; border-bottom: 1px solid var(--rbp-line); }
                .rbp-pillar:first-child { padding-top: 0; }
                .rbp-pillar:last-child { border-bottom: 0; }

                /* 11. 마무리 */
                .rbp-closing { padding-bottom: 96px; }
                .rbp-closing__title {
                    margin: 0; font-size: clamp(36px, 4.6vw, 60px); line-height: 1.12;
                    font-weight: 800; letter-spacing: -0.03em; color: var(--rbp-ink);
                }
                .rbp-closing__body { font-size: 16px; }
                .rbp-closing .rbp-cols--2 { column-gap: 48px; row-gap: 16px; }
                .rbp-closing__cta {
                    display: flex; justify-content: space-between; align-items: center; gap: 32px;
                    margin-top: 80px; padding: 40px 48px; border-radius: 16px; background: var(--rbp-ink); color: #fff;
                }
                .rbp-closing__cta-title { margin: 0 0 10px; font-size: 24px; font-weight: 800; }
                .rbp-closing__cta-sub { margin: 0; font-size: 14px; line-height: 1.75; color: rgba(255, 255, 255, 0.7); }
                .rbp-closing__cta-sub a { color: #fff; font-weight: 700; text-decoration: none; }

                @media (max-width: 1024px) {
                    .rbp-cols--4 { grid-template-columns: repeat(2, minmax(0, 1fr)); row-gap: 48px; }
                    .rbp-cols--4 .rbp-col:nth-child(3) { padding-left: 0; border-left: 0; }
                    .rbp-contents__list { grid-template-columns: repeat(2, minmax(0, 1fr)); row-gap: 40px; }
                    .rbp-steps { grid-template-columns: repeat(2, minmax(0, 1fr)); row-gap: 48px; }
                    .rbp-step:nth-child(3) { padding-left: 0; border-left: 0; }
                    .rbp-step:nth-child(2) .rbp-step__head::after { right: 0; }
                    .rbp-security, .rbp-expertise, .rbp-gap { grid-template-columns: minmax(0, 1fr); gap: 48px; }
                    .rbp-closing__cta { flex-direction: column; align-items: flex-start; }
                }

                @media (max-width: 768px) {
                    .rbp-container { padding: 0 20px; }
                    .rbp-section { padding: 72px 0; }
                    .rbp-hero { padding: 32px 0 64px; }
                    .rbp-hero__grid { grid-template-columns: minmax(0, 1fr); gap: 32px; padding: 40px 0 32px; }
                    .rbp-hero__title { font-size: clamp(72px, 24vw, 120px); }
                    .rbp-hero__bottom { gap: 16px; align-items: flex-start; }
                    .rbp-hero__bottom .rbp-rule__line { display: none; }
                    .rbp-hero__quote { font-size: 14px; }
                    .rbp-hero__quote br { display: none; }
                    .rbp-cta { width: 100%; }
                    .rbp-cta .rbp-btn { flex: 1 1 100%; }

                    .rbp-consult-band { padding: 56px 0 64px; }
                    .rbp-contents { padding: 64px 0; }
                    .rbp-contents__head { flex-direction: column; gap: 16px; margin-bottom: 40px; }
                    .rbp-contents__tag { text-align: left; }

                    .rbp-split { grid-template-columns: minmax(0, 1fr); gap: 24px; margin-bottom: 40px; }
                    .rbp-split__aside { align-items: flex-start; }
                    .rbp-aside-text { text-align: left; }
                    .rbp-rule { margin: 24px 0 36px; }
                    .rbp-title br, .rbp-title--lg br { display: none; }
                    .rbp-title--en br { display: inline; }

                    .rbp-cols--4, .rbp-cols--3, .rbp-cols--2 { grid-template-columns: minmax(0, 1fr); row-gap: 0; }
                    .rbp-col, .rbp-col:first-child, .rbp-cols--4 .rbp-col:nth-child(3) {
                        padding: 28px 0; border-left: 0; border-top: 1px solid var(--rbp-line);
                    }
                    .rbp-col:first-child { padding-top: 0; border-top: 0; }
                    .rbp-cols--top { margin-top: 40px; }

                    .rbp-scale { grid-template-columns: minmax(0, 1fr); }
                    .rbp-scale__cell + .rbp-scale__cell { border-left: 0; border-top: 1px solid var(--rbp-line); }
                    .rbp-scale__cell { padding: 18px 16px; }

                    .rbp-steps { grid-template-columns: minmax(0, 1fr); row-gap: 0; }
                    .rbp-step, .rbp-step + .rbp-step, .rbp-step:nth-child(3) {
                        display: grid; grid-template-columns: 48px minmax(0, 1fr); column-gap: 20px;
                        padding: 0 0 32px; border-left: 0;
                    }
                    .rbp-step__head { grid-row: span 3; margin: 0; }
                    .rbp-step__head::after, .rbp-step + .rbp-step .rbp-step__head::after {
                        left: 24px; right: auto; top: 58px; bottom: -22px; width: 1px; height: auto;
                    }
                    .rbp-step:last-child .rbp-step__head::after { display: none; }
                    .rbp-step__head { align-self: stretch; }
                    .rbp-step:last-child { padding-bottom: 0; }

                    .rbp-security__list li { grid-template-columns: 32px minmax(0, 1fr); row-gap: 4px; padding: 20px 0; }
                    .rbp-security__body { grid-column: 2; }

                    .rbp-process { grid-template-columns: minmax(0, 1fr); row-gap: 0; }
                    .rbp-process__item { display: flex; align-items: center; gap: 16px; padding: 14px 0; border-bottom: 1px solid var(--rbp-line); }
                    .rbp-process__item::before { display: none; }
                    .rbp-process__name { margin: 0; font-size: 15px; }
                    .rbp-process__name br { display: none; }

                    .rbp-pillar { gap: 16px; }
                    .rbp-closing__cta { margin-top: 48px; padding: 28px 20px; }
                    .rbp-closing__cta-title { font-size: 20px; }
                }
            `}</style>
        </>
    );
}
